import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Star, Heart, Leaf, Truck, Award } from "lucide-react";
import { api } from "../lib/api";
import { useApp } from "../context/AppContext";
import ProductCard from "../components/ProductCard";

const whyIcons = [Award, Heart, Truck, Leaf];

export default function Home() {
    const { homepage } = useApp();
    const [newArrivals, setNewArrivals] = useState([]);
    const [featured, setFeatured] = useState([]);
    const [reviews, setReviews] = useState([]);

    useEffect(() => {
        api.get("/products?is_new=true&limit=8").then(r => setNewArrivals(r.data));
        api.get("/products?is_featured=true&limit=4").then(r => setFeatured(r.data));
        api.get("/reviews").then(r => setReviews(r.data));
    }, []);

    if (!homepage) return <div className="min-h-screen bg-[color:var(--ms-ivory)]" />;

    return (
        <div className="bg-[color:var(--ms-ivory)]" data-testid="home-page">
            {/* HERO */}
            <section className="relative min-h-[80vh] md:min-h-[86vh] overflow-hidden" data-testid="hero-section">
                <img
                    src={homepage.hero_image_url}
                    alt="Mini Style — collezione bambini"
                    className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-black/5 to-[color:var(--ms-ivory)]" />
                <div className="relative z-10 h-full min-h-[80vh] md:min-h-[86vh] px-6 md:px-12 lg:px-24 flex flex-col justify-end pb-20 md:pb-24">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.9 }}
                    >
                        <div className="overline mb-5 text-[color:var(--ms-warm-white)]" data-testid="hero-overline">Nuova Collezione · Autunno / Inverno</div>
                        <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl xl:text-8xl tracking-tight font-medium leading-[0.98] text-[color:var(--ms-warm-white)] max-w-4xl drop-shadow-sm" data-testid="hero-headline">
                            {homepage.hero_headline}
                        </h1>
                        <p className="text-[color:var(--ms-warm-white)]/95 mt-6 max-w-xl text-base md:text-lg font-light" data-testid="hero-subheadline">
                            {homepage.hero_subheadline}
                        </p>
                        <div className="flex flex-wrap gap-3 mt-10">
                            <Link
                                to={homepage.hero_cta_primary_link}
                                className="bg-[color:var(--ms-charcoal)] text-[color:var(--ms-warm-white)] px-8 py-4 text-xs tracking-[0.22em] font-medium hover:bg-[color:var(--ms-sage-dark)] transition-all duration-300 flex items-center gap-2 group"
                                data-testid="hero-cta-primary"
                            >
                                {homepage.hero_cta_primary} <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                            </Link>
                            <Link
                                to={homepage.hero_cta_secondary_link}
                                className="bg-[color:var(--ms-warm-white)]/95 backdrop-blur text-[color:var(--ms-charcoal)] px-8 py-4 text-xs tracking-[0.22em] font-medium hover:bg-[color:var(--ms-warm-white)] transition-all duration-300"
                                data-testid="hero-cta-secondary"
                            >
                                {homepage.hero_cta_secondary}
                            </Link>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* TRUST BANNER */}
            <section className="border-y border-[color:var(--ms-border)] bg-[color:var(--ms-cream)]" data-testid="trust-banner">
                <div className="px-6 md:px-12 lg:px-24 py-5 grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                    {homepage.trust_items?.map((item, i) => (
                        <div key={i} className="text-[color:var(--ms-charcoal)] text-xs tracking-[0.18em] uppercase flex items-center justify-center gap-2 font-medium">
                            <span className="text-[color:var(--ms-sage-dark)]">✓</span> {item}
                        </div>
                    ))}
                </div>
            </section>

            {/* SHOP BY AGE */}
            <section className="px-6 md:px-12 lg:px-24 py-24" data-testid="shop-by-age">
                <div className="text-center mb-14">
                    <div className="overline mb-3">Scegli per età</div>
                    <h2 className="font-display text-4xl sm:text-5xl tracking-tight text-[color:var(--ms-charcoal)]">Ogni età, il suo stile.</h2>
                    <p className="text-[color:var(--ms-muted)] text-sm mt-3 max-w-lg mx-auto">Dai primi passi ai 16 anni. Selezione curata per ogni fase.</p>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2 md:gap-3">
                    {homepage.shop_by_age?.map((b) => (
                        <Link
                            key={b.slug}
                            to={`/collection/${b.slug}`}
                            className="relative aspect-[3/4] overflow-hidden bg-[color:var(--ms-cream)] group"
                            data-testid={`age-card-${b.slug}`}
                        >
                            <img src={b.image} alt={b.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-all duration-700" />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
                            <div className="absolute bottom-5 left-5 right-5">
                                <div className="font-display text-2xl md:text-3xl text-[color:var(--ms-warm-white)] leading-tight">{b.name}</div>
                                {b.age && <div className="text-[10px] tracking-[0.24em] uppercase text-[color:var(--ms-warm-white)]/90 mt-1.5">{b.age}</div>}
                            </div>
                        </Link>
                    ))}
                </div>
            </section>

            {/* NUOVI ARRIVI */}
            <section className="px-6 md:px-12 lg:px-24 py-20 bg-[color:var(--ms-cream)]" data-testid="new-arrivals">
                <div className="flex items-end justify-between mb-12 gap-4">
                    <div>
                        <div className="overline mb-3">Appena arrivati</div>
                        <h2 className="font-display text-4xl sm:text-5xl tracking-tight text-[color:var(--ms-charcoal)]">Nuovi Arrivi</h2>
                    </div>
                    <Link to="/collection/nuovi-arrivi" className="text-xs tracking-[0.2em] uppercase text-[color:var(--ms-sage-dark)] link-underline hidden md:inline-block" data-testid="new-arrivals-view-all">SCOPRI TUTTO</Link>
                </div>
                {newArrivals.length === 0 ? (
                    <EmptyGrid label="Presto nuovi arrivi. Iscriviti alla newsletter per essere il primo a saperlo." />
                ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-10">
                        {newArrivals.map(p => <ProductCard key={p.id} product={p} />)}
                    </div>
                )}
            </section>

            {/* COMPLETI editorial */}
            <section className="border-b border-[color:var(--ms-border)]" data-testid="completi-section">
                <div className="grid md:grid-cols-2 min-h-[60vh]">
                    <div className="bg-[color:var(--ms-ivory)] flex items-center px-8 md:px-16 lg:px-24 py-20 order-2 md:order-1">
                        <div className="max-w-md">
                            <div className="overline mb-4">Completi</div>
                            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.02] text-[color:var(--ms-charcoal)]">
                                {homepage.completi_title}
                            </h2>
                            <p className="text-[color:var(--ms-muted)] mt-6 text-base font-light">{homepage.completi_subtitle}</p>
                            <Link
                                to={homepage.completi_link}
                                className="inline-flex items-center gap-2 mt-10 border border-[color:var(--ms-charcoal)] text-[color:var(--ms-charcoal)] px-8 py-4 text-xs tracking-[0.22em] font-medium hover:bg-[color:var(--ms-charcoal)] hover:text-[color:var(--ms-warm-white)] transition-all group"
                                data-testid="completi-cta"
                            >
                                {homepage.completi_cta} <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                            </Link>
                        </div>
                    </div>
                    <div className="relative overflow-hidden bg-[color:var(--ms-cream)] min-h-[60vh] order-1 md:order-2">
                        <img src={homepage.completi_image} alt="Completi Mini Style" className="absolute inset-0 w-full h-full object-cover" />
                    </div>
                </div>
            </section>

            {/* CLOTHING / FOOTWEAR split */}
            <section className="grid md:grid-cols-2" data-testid="clothing-footwear-split">
                <Link to={homepage.clothing_link} className="relative aspect-[4/5] md:aspect-auto md:min-h-[500px] overflow-hidden group" data-testid="clothing-card">
                    <img src={homepage.clothing_image} alt={homepage.clothing_title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-transparent" />
                    <div className="absolute bottom-8 left-8 right-8">
                        <div className="overline text-[color:var(--ms-warm-white)]/90 mb-2">Categoria</div>
                        <h3 className="font-display text-4xl md:text-5xl text-[color:var(--ms-warm-white)] leading-none">{homepage.clothing_title}</h3>
                        <span className="inline-flex items-center gap-2 mt-4 text-[color:var(--ms-warm-white)] text-xs tracking-[0.22em] uppercase link-underline">Scopri <ArrowRight size={12} /></span>
                    </div>
                </Link>
                <Link to={homepage.footwear_link} className="relative aspect-[4/5] md:aspect-auto md:min-h-[500px] overflow-hidden group" data-testid="footwear-card">
                    <img src={homepage.footwear_image} alt={homepage.footwear_title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-transparent" />
                    <div className="absolute bottom-8 left-8 right-8">
                        <div className="overline text-[color:var(--ms-warm-white)]/90 mb-2">Categoria</div>
                        <h3 className="font-display text-4xl md:text-5xl text-[color:var(--ms-warm-white)] leading-none">{homepage.footwear_title}</h3>
                        <span className="inline-flex items-center gap-2 mt-4 text-[color:var(--ms-warm-white)] text-xs tracking-[0.22em] uppercase link-underline">Scopri <ArrowRight size={12} /></span>
                    </div>
                </Link>
            </section>

            {/* WHY MINI STYLE */}
            <section className="px-6 md:px-12 lg:px-24 py-24" data-testid="why-section">
                <div className="text-center mb-16 max-w-2xl mx-auto">
                    <div className="overline mb-3">Perché sceglierci</div>
                    <h2 className="font-display text-4xl sm:text-5xl tracking-tight text-[color:var(--ms-charcoal)]">Un&apos;esperienza pensata per le famiglie.</h2>
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                    {homepage.why_items?.map((w, i) => {
                        const Icon = whyIcons[i % whyIcons.length];
                        return (
                            <div key={i} className="panel p-8 hover:border-[color:var(--ms-sage)] transition-colors duration-500 group" data-testid={`why-card-${i}`}>
                                <div className="w-12 h-12 rounded-full bg-[color:var(--ms-cream)] flex items-center justify-center mb-6 group-hover:bg-[color:var(--ms-sage)]/20 transition-colors">
                                    <Icon size={20} className="text-[color:var(--ms-sage-dark)]" />
                                </div>
                                <h3 className="font-display text-xl text-[color:var(--ms-charcoal)] mb-3">{w.title}</h3>
                                <p className="text-sm text-[color:var(--ms-muted)] leading-relaxed">{w.text}</p>
                            </div>
                        );
                    })}
                </div>
            </section>

            {/* PREFERITI */}
            {featured.length > 0 && (
                <section className="px-6 md:px-12 lg:px-24 py-20 border-t border-[color:var(--ms-border)] bg-[color:var(--ms-cream)]" data-testid="preferiti-section">
                    <div className="flex items-end justify-between mb-12 gap-4">
                        <div>
                            <div className="overline mb-3">Selezione</div>
                            <h2 className="font-display text-4xl sm:text-5xl tracking-tight text-[color:var(--ms-charcoal)]">I Nostri Preferiti</h2>
                        </div>
                        <Link to="/shop" className="text-xs tracking-[0.2em] uppercase text-[color:var(--ms-sage-dark)] link-underline hidden md:inline-block">TUTTA LA COLLEZIONE</Link>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-10">
                        {featured.map(p => <ProductCard key={p.id} product={p} />)}
                    </div>
                </section>
            )}

            {/* FEATURED COLLECTION */}
            <section className="grid md:grid-cols-2 min-h-[60vh]" data-testid="featured-drop">
                <div className="relative overflow-hidden bg-[color:var(--ms-cream)] min-h-[50vh]">
                    <img src={homepage.featured_drop_image} alt="collezione in evidenza" className="absolute inset-0 w-full h-full object-cover" />
                </div>
                <div className="bg-[color:var(--ms-ivory)] flex items-center px-8 md:px-16 lg:px-24 py-20">
                    <div className="max-w-md">
                        <div className="overline mb-4">In Evidenza</div>
                        <h2 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.02] text-[color:var(--ms-charcoal)]">
                            {homepage.featured_drop_title}
                        </h2>
                        <p className="text-[color:var(--ms-muted)] mt-6 text-base font-light">{homepage.featured_drop_subtitle}</p>
                        <Link to={homepage.featured_drop_link} className="inline-flex items-center gap-2 mt-10 bg-[color:var(--ms-charcoal)] text-[color:var(--ms-warm-white)] px-8 py-4 text-xs tracking-[0.22em] font-medium hover:bg-[color:var(--ms-sage-dark)] transition-all group" data-testid="featured-drop-cta">
                            SCOPRI ORA <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                        </Link>
                    </div>
                </div>
            </section>

            {/* REVIEWS */}
            <section className="px-6 md:px-12 lg:px-24 py-24" data-testid="reviews-section">
                <div className="text-center mb-16 max-w-2xl mx-auto">
                    <div className="overline mb-3">Le famiglie che ci scelgono</div>
                    <h2 className="font-display text-4xl sm:text-5xl tracking-tight text-[color:var(--ms-charcoal)]">Le loro parole.</h2>
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                    {reviews.map((r, i) => (
                        <div key={r.id} className="panel p-8 hover:border-[color:var(--ms-sage)] transition-colors duration-500" data-testid={`review-card-${i}`}>
                            <div className="flex gap-1 text-[color:var(--ms-ochre)] mb-4">
                                {Array.from({ length: r.rating || 5 }).map((_, j) => <Star key={j} size={14} fill="currentColor" />)}
                            </div>
                            <p className="font-display text-lg text-[color:var(--ms-charcoal)] leading-snug mb-6">&ldquo;{r.text}&rdquo;</p>
                            <div className="overline">— {r.name}</div>
                        </div>
                    ))}
                </div>
            </section>

            {/* INSTAGRAM (TikTok removed as request focused on Instagram) */}
            <section className="px-6 md:px-12 lg:px-24 py-20 border-t border-[color:var(--ms-border)] bg-[color:var(--ms-cream)]" data-testid="social-section">
                <div className="grid md:grid-cols-2 gap-12 items-center">
                    <div>
                        <div className="overline mb-3">Community</div>
                        <h2 className="font-display text-4xl sm:text-5xl tracking-tight text-[color:var(--ms-charcoal)]">Seguici su Instagram</h2>
                        <p className="text-[color:var(--ms-muted)] mt-3 text-base font-light max-w-md">
                            Nuovi arrivi, dietro le quinte e outfit del giorno. Condividi il tuo <span className="font-italic-display">Mini Style</span> con
                            <span className="text-[color:var(--ms-sage-dark)] ml-1">#ministyleitalia</span>.
                        </p>
                        {homepage.footer_instagram && (
                            <a href={homepage.footer_instagram} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 mt-8 border border-[color:var(--ms-charcoal)] text-[color:var(--ms-charcoal)] px-6 py-3 text-xs tracking-[0.22em] uppercase font-medium hover:bg-[color:var(--ms-charcoal)] hover:text-[color:var(--ms-warm-white)] transition-colors" data-testid="instagram-cta">
                                Vai a {homepage.instagram_handle} <ArrowRight size={12} />
                            </a>
                        )}
                    </div>
                    <div className="panel p-6 md:p-8 min-h-[280px] flex flex-col justify-center" data-testid="instagram-section">
                        {homepage.instagram_embed ? (
                            <div dangerouslySetInnerHTML={{ __html: homepage.instagram_embed }} />
                        ) : (
                            <div className="grid grid-cols-3 gap-2">
                                {[
                                    "https://customer-assets-39nsmqrw.emergentagent.net/job_rare-finds-club/artifacts/31b0m4ps_IMG_9134.jpg",
                                    "https://customer-assets-39nsmqrw.emergentagent.net/job_rare-finds-club/artifacts/murobhk6_IMG_9132.jpg",
                                    "https://customer-assets-39nsmqrw.emergentagent.net/job_rare-finds-club/artifacts/6qrm0gd8_IMG_9131.jpg",
                                    "https://customer-assets-39nsmqrw.emergentagent.net/job_rare-finds-club/artifacts/6ylqj1mz_IMG_9130.jpg",
                                    "https://customer-assets-39nsmqrw.emergentagent.net/job_rare-finds-club/artifacts/ga1r6waq_IMG_9129.jpg",
                                    "https://customer-assets-39nsmqrw.emergentagent.net/job_rare-finds-club/artifacts/t2u3k0vs_IMG_9078.jpg",
                                ].map((src, i) => (
                                    <a
                                        key={i}
                                        href={homepage.footer_instagram || "#"}
                                        target="_blank"
                                        rel="noreferrer"
                                        aria-label={`Post Instagram ${i + 1}`}
                                        className="aspect-square bg-[color:var(--ms-cream)] overflow-hidden group"
                                        data-testid={`instagram-tile-${i}`}
                                    >
                                        <img
                                            src={src}
                                            alt={`Mini Style Instagram ${i + 1}`}
                                            loading="lazy"
                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                        />
                                    </a>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </section>
        </div>
    );
}

const EmptyGrid = ({ label }) => (
    <div className="panel py-20 text-center text-[color:var(--ms-muted)] text-sm px-6" data-testid="empty-grid">
        {label}
    </div>
);
